/**
 * Types for the Developer Help "recipe card" content modules.
 *
 * Recipes follow a fixed skeleton (goal, time, ingredients, steps, taste test,
 * common burns) so every guide reads the same way. Content lives in recipes.ts;
 * pages only render.
 */

export type RecipeLanguage = 'curl' | 'python' | 'csharp';

export const RECIPE_LANGUAGE_LABELS: Record<RecipeLanguage, string> = {
  curl: 'cURL',
  python: 'Python',
  csharp: 'C#',
};

/** A code example, optionally offered in multiple languages via tabs. */
export type RecipeCodeSample = {
  /** Short caption rendered above the code block. */
  title?: string;
  /** Language → snippet. Single-entry samples render without tabs. */
  languages: Partial<Record<RecipeLanguage, string>>;
};

/** One numbered step inside a recipe. */
export type RecipeStep = {
  title: string;
  /** Plain-text body; rendered as a paragraph. */
  body: string;
  code?: RecipeCodeSample;
};

/** A full recipe card (one article). */
export type Recipe = {
  /** URL segment under /help/developers/:slug */
  slug: string;
  /** Recipe number shown on the card (0-based to match the docs series). */
  number: number;
  title: string;
  /** e.g. "~10 min" */
  time: string;
  /** One-sentence outcome. */
  goal: string;
  /** Prerequisites checklist. */
  ingredients: string[];
  steps: RecipeStep[];
  /** How to verify it worked. */
  tasteTest: string[];
  /** Troubleshooting: "common burns". */
  burns: string[];
  /** Slug of the next recipe in the journey, if any. */
  nextSlug?: string;
};
